import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import type { FunctionComponent } from "react";
import { useCallback, useEffect, useState } from "react";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { notEmpty } from "shared/lib/utils";

import { googleImaAtom } from "scmp-app/components/advertisement/google-ima/atoms";

import { VideoPlayMode } from "./enums";
import type { VideoTitleContainerProps } from "./scmp-youtube-video-footer/styles";
import type { Props as SCMPYouTubeVideoPlayerProps } from "./scmp-youtube-video-player";
import { SCMPYouTubeVideoPlayer } from "./scmp-youtube-video-player";
import {
  Container,
  PlayerContainer,
  StyledSCMPYouTubeVideoFooter,
  StyledSCMPYoutubeVideoPreview,
} from "./styles";

export type Props = Omit<
  SCMPYouTubeVideoPlayerProps,
  "queryReference" | "videoId" | "videoPlayMode"
> & {
  className?: string;
  duration?: string;
  enableAutoPlay: boolean;
  imageSource?: string;
  index: number;
  initialVideoPlayMode?: VideoPlayMode;
  isEnabledImaManager: boolean;
  isMuteVideo?: boolean;
  withFooter?: { variant: VideoTitleContainerProps["$variant"]; withIcon?: boolean };
  youtubeVideoId?: string;
};

const Component: FunctionComponent<Props> = ({
  className,
  duration,
  enableAutoPlay,
  height,
  imageSource,
  index = 0,
  initialVideoPlayMode = VideoPlayMode.Unset,
  isEnabledImaManager,
  isMuteVideo = true,
  title,
  width,
  withFooter,
  youtubeVideoId,
  ...attribs
}: Props) => {
  const { isImaReady } = useAtomValue(googleImaAtom);

  const hasVideoId = notEmpty(youtubeVideoId);
  const [videoPlayMode, setVideoPlayMode] = useState(initialVideoPlayMode);
  const [shouldShowPlayer, setShouldShowPlayer] = useState(false);
  const [shouldShowPreview, setShouldShowPreview] = useState(true);

  const orientation =
    attribs.orientation ??
    (height && width && Number.parseInt(height, 10) > Number.parseInt(width, 10)
      ? "portrait"
      : "landscape");

  const handlePlaybackVisible = useCallback(() => {
    setShouldShowPlayer(true);
  }, []);

  const handlePreviewClick = useCallback(() => {
    setVideoPlayMode(VideoPlayMode.Manual);
  }, []);

  useEffect(() => {
    if (!hasVideoId || !isImaReady || !shouldShowPlayer) return;
    setShouldShowPreview(false);
  }, [hasVideoId, isImaReady, shouldShowPlayer]);

  useEffect(() => {
    if (!enableAutoPlay) return;
    setVideoPlayMode(currentMode =>
      currentMode === VideoPlayMode.Unset ? VideoPlayMode.Autoplay : currentMode,
    );
  }, [enableAutoPlay]);

  return (
    <Container className={className}>
      <PlayerContainer $orientation={orientation}>
        {hasVideoId && (
          <ClientSideSuspense>
            <SCMPYouTubeVideoPlayer
              enableAutoPlay={enableAutoPlay}
              height={height}
              index={index}
              isEnabledImaManager={isEnabledImaManager}
              isMuteVideo={isMuteVideo}
              onPlaybackVisible={handlePlaybackVisible}
              orientation={orientation}
              title={title}
              videoId={youtubeVideoId}
              videoPlayMode={videoPlayMode}
              width={width}
              {...attribs}
            />
          </ClientSideSuspense>
        )}
        {shouldShowPreview && (
          <StyledSCMPYoutubeVideoPreview
            duration={duration}
            imageSource={imageSource}
            onClick={handlePreviewClick}
            orientation={orientation}
          />
        )}
      </PlayerContainer>
      {withFooter && (
        <StyledSCMPYouTubeVideoFooter
          $orientation={orientation}
          title={title}
          variant={withFooter.variant}
          withIcon={withFooter.withIcon}
        />
      )}
    </Container>
  );
};

Component.displayName = "SCMPYoutubeVideo";
export const SCMPYoutubeVideo = styled(Component)``;
